import { WEIGHT_TREND } from './config';
import { daysBetween } from './dates';
import { decayPerDay, round6 } from './math';
import type { BodyWeight } from './types';

export interface TrendPoint {
  date: string;
  kg: number;
  /** Media móvil exponencial: el peso "de verdad", sin el ruido del día. */
  trend: number;
}

/**
 * Tendencia suavizada del peso corporal (SPEC 6.D).
 *
 * El peso diario sube y baja un kilo largo por agua, sal y digestión. Tomar
 * decisiones con el dato crudo lleva a cambiar la dieta por ruido, así que
 * todo lo que decide el motor mira esta tendencia, no la báscula de hoy.
 *
 * El decaimiento se calcula por DÍAS TRANSCURRIDOS y no por número de medidas:
 * quien se pesa una vez por semana no debe ver cómo un solo dato arrastra la
 * tendencia igual que siete seguidos.
 */
export function weightTrend(weights: readonly BodyWeight[]): TrendPoint[] {
  if (weights.length === 0) return [];

  const sorted = [...weights].sort((a, b) => a.date.localeCompare(b.date));
  const perDay = decayPerDay(WEIGHT_TREND.halfLifeDays);

  const out: TrendPoint[] = [];
  let trend = sorted[0]!.kg;
  let previousDate = sorted[0]!.date;

  for (const point of sorted) {
    const elapsed = Math.max(daysBetween(previousDate, point.date), 0);
    const weight = Math.pow(perDay, elapsed);
    // Cuanto más tiempo ha pasado, menos manda el valor anterior.
    trend = trend * weight + point.kg * (1 - weight);
    previousDate = point.date;
    out.push({ date: point.date, kg: point.kg, trend: round6(trend) });
  }

  return out;
}

/**
 * Cambio DIARIO de la tendencia, en kg/día, medido sobre los últimos
 * `windowDays` días. Negativo al perder peso.
 *
 * Es la entrada del TDEE adaptativo: multiplicado por 7700 kcal/kg da el
 * desajuste energético real (SPEC 6.A).
 */
export function dailyTrendChange(points: readonly TrendPoint[], windowDays: number): number | null {
  if (points.length < WEIGHT_TREND.minPointsForRate) return null;

  const last = points.at(-1)!;
  const cutoff = daysBetween(points[0]!.date, last.date);
  if (cutoff < windowDays) return null;

  // El primer punto dentro de la ventana.
  const first = points.find((p) => daysBetween(p.date, last.date) <= windowDays);
  if (!first || first.date === last.date) return null;

  const elapsed = daysBetween(first.date, last.date);
  if (elapsed <= 0) return null;

  return round6((last.trend - first.trend) / elapsed);
}
